import { formatUtils } from '@form-factory/shared';
import { useLingui } from '@lingui/react/macro';
import { Link, useNavigate } from '@tanstack/react-router';
import { Avatar, Dropdown, Separator, toast } from '@utima/ui';
import {
  Settings,
  ChevronsUpDown,
  SquareArrowLeft,
  CreditCard,
  UserPen,
} from 'lucide-react';
import { memo } from 'react';

import { AppSettings } from '@/constants/AppSettings';
import { useUser } from '@/hooks';
import { trpc } from '@/services/trpc';

export const UserDropdown = memo(function UserDropdown() {
  const navigate = useNavigate();
  const { t } = useLingui();

  const { logout } = useUser();
  const { user, currentOrganizationId } = useUser();
  const logoutMutation = trpc.auth.logout.useMutation({
    onError: error => {
      toast.error(t`Logout failed`, {
        description: t`Failed to logout.`,
      });
    },
    onSettled: () => {
      logout();
      navigate({ to: '/login' });
    },
  });

  if (!user) {
    return null;
  }

  if (!currentOrganizationId) {
    navigate({ to: '/login' });
  }

  return (
    <div className='mt-6 flex w-full flex-col items-center gap-3'>
      <div className='flex items-center gap-2'>
        <p className='text-xs font-medium text-menu-fg/50'>
          v{AppSettings.version}
        </p>
      </div>
      <Separator />
      <Dropdown.Root modal={false}>
        <Dropdown.Trigger
          data-testid='user-dropdown-trigger'
          className='group flex w-full items-center justify-between gap-3 rounded-radius p-2 transition-all hover:bg-accent'
        >
          <div className='flex items-center gap-3'>
            <Avatar
              className='cursor-pointer'
              src={user?.photo}
              fallback={formatUtils.initials(
                formatUtils.fullName({
                  firstName: user?.firstName || '',
                  lastName: user?.lastName || '',
                }),
              )}
            />
            <div className='grow text-left'>
              <p className='text-sm font-medium'>
                {formatUtils.fullName({
                  firstName: user.firstName || user.email,
                  lastName: user.lastName || '',
                })}
              </p>
              <p className='text-xs text-foreground/50'>{user.email}</p>
            </div>
          </div>
          <div className='flex shrink-0 items-center justify-center'>
            <ChevronsUpDown className='size-4' />
          </div>
        </Dropdown.Trigger>
        <Dropdown.Content side='top' align='end' className='min-w-52'>
          <Dropdown.Label>{t`Organization`}</Dropdown.Label>
          <Dropdown.Item className='flex items-center gap-2' asChild>
            <Link
              to='/organizations/$organizationId/edit'
              params={{ organizationId: currentOrganizationId ?? '' }}
            >
              <Settings className='size-4' />
              {t`Settings`}
            </Link>
          </Dropdown.Item>
          <Dropdown.Item className='flex items-center gap-2' asChild>
            <Link
              to='/organizations/$organizationId/payments'
              params={{ organizationId: currentOrganizationId ?? '' }}
            >
              <CreditCard className='size-4' />
              {t`Payments`}
            </Link>
          </Dropdown.Item>
          <Dropdown.Separator />
          <Dropdown.Label>{t`User`}</Dropdown.Label>
          <Dropdown.Item className='flex items-center gap-2' asChild>
            <Link to='/users/profile'>
              <UserPen className='size-4' />
              {t`Profile`}
            </Link>
          </Dropdown.Item>
          <Dropdown.Item
            className='flex items-center gap-2'
            onClick={() => logoutMutation.mutate()}
          >
            <SquareArrowLeft className='size-4' />
            {t`Logout`}
          </Dropdown.Item>
        </Dropdown.Content>
      </Dropdown.Root>
    </div>
  );
});
